<template>
  <div class="page-container vip-rate-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <span class="page-title">VIP管理</span>
      </el-breadcrumb-item>
      <el-breadcrumb-item>VIP费率管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <div class="table-description">
        配置手续费折扣，参考基础费率为现货Maker：0.1%、现货Taker：0.1%、合约Maker：0.04%、合约Taker：0.06%，具体费率由系统自动计算
      </div>

      <el-table :data="rows" border size="small" style="width: 100%">
        <el-table-column prop="vipLevel" label="VIP等级" width="100" align="center">
          <template slot-scope="scope">VIP{{ scope.row.vipLevel }}</template>
        </el-table-column>
        <el-table-column
          v-for="type in rateTypes"
          :key="type.code"
          min-width="170"
          align="center"
        >
          <template slot="header">
            <div>{{ type.label }}</div>
            <div>手续费折扣/具体费率</div>
          </template>
          <template slot-scope="scope">
            <div>{{ rateCell(scope.row, type) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column prop="createBy" label="创建人" width="120" />
        <el-table-column label="更新时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
        <el-table-column prop="updateBy" label="更新人" width="120" />
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button v-if="canEdit" type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
            <el-button v-if="canHistory" type="text" size="small" @click="openHistory(scope.row)">历史记录</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50]"
        @size-change="onSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <el-dialog
      :title="`编辑VIP${form.vipLevel}费率`"
      :visible.sync="editVisible"
      width="860px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form :model="form" size="small">
        <div class="section-title">费率管理</div>
        <div
          v-for="group in rateGroups"
          :key="group.code"
          class="rate-market-group"
        >
          <div class="rate-market-title">{{ group.label }}</div>
          <div class="rate-grid">
            <div
              v-for="(type, index) in group.types"
              :key="type.code"
              class="rate-column"
              :class="{ 'rate-column-divider': index > 0 }"
            >
              <div class="rate-column-title">
                {{ rateRoleLabel(type) }}手续费费率
                <span class="required-mark">*</span>
              </div>
              <div class="rate-field-label">
                手续费折扣
                <span class="required-mark">*</span>
              </div>
              <el-input
                v-model.trim="rateForm(type.code).discountPercent"
                class="rate-input"
                placeholder="请输入手续费折扣"
                @input="onRateInput(type.code, $event)"
              >
                <template slot="append">%</template>
              </el-input>
              <div class="rate-field-label">具体手续费率</div>
              <el-input
                :value="specificFeeRate(type)"
                class="rate-input specific-rate-input"
                disabled
              />
            </div>
          </div>
        </div>
        <div class="rate-explanation">
          <i class="el-icon-info rate-explanation-icon"></i>
          <span>{{ rateExplanation }}</span>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="editVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmSubmit">确认修改</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="`VIP${historyVipLevel || ''}费率历史记录`"
      :visible.sync="historyVisible"
      width="760px"
      @closed="resetHistory"
    >
      <el-table :data="historyRows" border size="small" style="width: 100%">
        <el-table-column label="修改时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
        <el-table-column label="修改项" width="120">
          <template slot-scope="scope">{{ rateTypeLabel(scope.row.rateType) }}</template>
        </el-table-column>
        <el-table-column label="修改前的值" min-width="170">
          <template slot-scope="scope">
            <div>折扣：{{ discountText(scope.row.beforeValue) }}</div>
            <div>费率：{{ feeText(scope.row.beforeValue, rateType(scope.row.rateType)) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="修改后的值" min-width="170">
          <template slot-scope="scope">
            <div>折扣：{{ discountText(scope.row.afterValue) }}</div>
            <div>费率：{{ feeText(scope.row.afterValue, rateType(scope.row.rateType)) }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="updateBy" label="修改人" width="120" />
      </el-table>
      <el-pagination
        class="history-pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="historyTotal"
        :page-size="historyPageSize"
        :current-page="historyPage"
        :page-sizes="[10, 20, 50]"
        @size-change="onHistorySizeChange"
        @current-change="onHistoryPageChange"
      />
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Message, MessageBox } from 'element-ui';
import BigNumber from 'bignumber.js';
import { hasPerm } from 'common/permission';
import api from './api';

function emptyForm() {
  return {
    vipLevelId: null,
    vipLevel: null,
    rates: {},
  };
}

export default {
  name: 'VipRatePage',
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      rateTypes: [],
      editVisible: false,
      historyVisible: false,
      historyVipLevelId: null,
      historyVipLevel: null,
      historyRows: [],
      historyTotal: 0,
      historyPage: 1,
      historyPageSize: 10,
      form: emptyForm(),
    };
  },
  computed: {
    ...mapState(['userInfo']),
    canEdit() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_rate:edit');
    },
    canHistory() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_rate:history');
    },
    rateGroups() {
      return [
        { code: 'SPOT', label: '现货手续费' },
        { code: 'CONTRACT', label: '合约手续费' },
      ].map((group) => ({
        ...group,
        types: this.rateTypes.filter((type) => String(type.code || '').startsWith(`${group.code}_`)),
      })).filter((group) => group.types.length);
    },
    rateExplanation() {
      const baseRates = this.rateTypes
        .filter((type) => type.baseRate !== null && type.baseRate !== undefined)
        .map((type) => `${type.label} ${this.baseFeeRate(type)}`)
        .join('、');
      const baseRateText = baseRates ? `，当前基础费率为${baseRates}` : '';
      return `说明：录入手续费折扣后，系统将按基础费率自动计算具体手续费率${baseRateText}；具体手续费率不支持修改。`;
    },
  },
  created() {
    this.loadEnums();
    this.fetchList();
  },
  methods: {
    async loadEnums() {
      const res = await api.enums();
      this.rateTypes = (res && res.data) || [];
    },
    async fetchList() {
      const res = await api.page({ page: this.page, size: this.pageSize });
      const data = (res && res.data) || {};
      this.rows = data.items || [];
      this.total = data.total || 0;
    },
    onSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchList();
    },
    rateType(code) {
      return this.rateTypes.find((type) => type.code === code) || {};
    },
    rateTypeLabel(code) {
      return this.rateType(code).label || code || '--';
    },
    rowRate(row, typeCode) {
      return (row.rates || []).find((item) => item.rateType === typeCode);
    },
    rateCell(row, type) {
      const item = this.rowRate(row, type.code);
      if (!item || item.discountRate == null) return '--';
      return `${this.discountText(item.discountRate)} / ${this.feeText(item.discountRate, type)}`;
    },
    discountText(value) {
      if (value == null || value === '') return '--';
      return `${this.trimNumber(new BigNumber(value).times(100))}%`;
    },
    feeText(discountRate, type) {
      if (discountRate == null || discountRate === '' || !type || type.baseRate == null) return '--';
      return `${this.trimNumber(new BigNumber(discountRate).times(type.baseRate).times(100))}%`;
    },
    trimNumber(num) {
      return num.decimalPlaces(8, BigNumber.ROUND_DOWN).toFixed();
    },
    formatTime(value) {
      if (!value) return '--';
      let d;
      if (Array.isArray(value)) {
        d = new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0);
      } else {
        d = new Date(value);
      }
      if (Number.isNaN(d.getTime())) return '--';
      const p = (x) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    openEdit(row) {
      this.form = emptyForm();
      this.form.vipLevelId = row.vipLevelId;
      this.form.vipLevel = row.vipLevel;
      this.rateTypes.forEach((type) => {
        const item = this.rowRate(row, type.code);
        this.$set(this.form.rates, type.code, {
          rateType: type.code,
          discountPercent: item && item.discountRate != null
            ? new BigNumber(item.discountRate).times(100).toFixed()
            : '',
        });
      });
      this.editVisible = true;
    },
    rateForm(typeCode) {
      if (!this.form.rates[typeCode]) {
        this.$set(this.form.rates, typeCode, { rateType: typeCode, discountPercent: '' });
      }
      return this.form.rates[typeCode];
    },
    onRateInput(typeCode, value) {
      this.rateForm(typeCode).discountPercent = this.sanitizeDecimalInput(value, 2);
    },
    rateRoleLabel(type) {
      const code = String((type && type.code) || '');
      if (code.endsWith('_MAKER')) return 'Maker';
      if (code.endsWith('_TAKER')) return 'Taker';
      return (type && type.label) || '';
    },
    baseFeeRate(type) {
      if (!type || type.baseRate === null || type.baseRate === undefined) return '--';
      const baseRate = new BigNumber(type.baseRate);
      if (!baseRate.isFinite()) return '--';
      return `${this.trimNumber(baseRate.times(100))}%`;
    },
    specificFeeRate(type) {
      const rate = type && this.form.rates[type.code];
      if (!rate || rate.discountPercent === '' || type.baseRate === null || type.baseRate === undefined) {
        return '--';
      }
      const discountPercent = new BigNumber(rate.discountPercent);
      const baseRate = new BigNumber(type.baseRate);
      if (!discountPercent.isFinite() || !baseRate.isFinite() || discountPercent.lte(0)) return '--';
      const actualPercent = baseRate.times(discountPercent.div(100)).times(100);
      return `${this.trimNumber(actualPercent)}%`;
    },
    sanitizeDecimalInput(value, maxDecimalPlaces) {
      const text = String(value == null ? '' : value).replace(/[^\d.]/g, '');
      const dotIndex = text.indexOf('.');
      if (dotIndex < 0) return text;
      const integerPart = text.slice(0, dotIndex) || '0';
      const decimalPart = text.slice(dotIndex + 1).replace(/\./g, '').slice(0, maxDecimalPlaces);
      return `${integerPart}.${decimalPart}`;
    },
    resetForm() {
      this.form = emptyForm();
    },
    validateForm() {
      for (const type of this.rateTypes) {
        const item = this.form.rates[type.code];
        if (!item || item.discountPercent === '') {
          Message.warning('请填写完整的VIP费率折扣');
          return false;
        }
        const discountPercent = String(item.discountPercent);
        if (!/^\d+(?:\.\d{1,2})?$/.test(discountPercent)
            || new BigNumber(discountPercent).lte(0)) {
          Message.warning('费率折扣必须大于0且最多支持2位小数');
          return false;
        }
      }
      return true;
    },
    confirmSubmit() {
      if (!this.validateForm()) return;
      MessageBox.confirm(`确认修改VIP${this.form.vipLevel}费率？`, '提示', { type: 'warning' })
        .then(() => this.doSubmit())
        .catch(() => {});
    },
    async doSubmit() {
      await api.update({
        vipLevelId: this.form.vipLevelId,
        rates: this.rateTypes.map((type) => ({
          rateType: type.code,
          discountRate: new BigNumber(this.form.rates[type.code].discountPercent).div(100).toFixed(),
        })),
      });
      Message.success('保存成功');
      this.editVisible = false;
      this.fetchList();
    },
    async openHistory(row) {
      this.historyVipLevelId = row.vipLevelId;
      this.historyVipLevel = row.vipLevel;
      this.historyPage = 1;
      this.historyPageSize = 10;
      await this.fetchHistory();
      this.historyVisible = true;
    },
    async fetchHistory() {
      if (!this.historyVipLevelId) return;
      const res = await api.history({
        vipLevelId: this.historyVipLevelId,
        page: this.historyPage,
        size: this.historyPageSize,
      });
      const data = (res && res.data) || {};
      this.historyRows = data.items || [];
      this.historyTotal = data.total || 0;
    },
    onHistorySizeChange(size) {
      this.historyPageSize = size;
      this.historyPage = 1;
      this.fetchHistory();
    },
    onHistoryPageChange(page) {
      this.historyPage = page;
      this.fetchHistory();
    },
    resetHistory() {
      this.historyVipLevelId = null;
      this.historyVipLevel = null;
      this.historyRows = [];
      this.historyTotal = 0;
      this.historyPage = 1;
      this.historyPageSize = 10;
    },
  },
};
</script>

<style scoped>
.page-content {
  margin-top: 12px;
}
.table-description {
  margin-bottom: 12px;
  color: #111c3d;
  font-size: 14px;
  font-weight: 500;
}
.section-title {
  margin: 8px 0 18px;
  color: #111c3d;
  font-weight: 600;
}
.rate-market-group {
  padding-bottom: 18px;
}
.rate-market-group + .rate-market-group {
  padding-top: 18px;
  border-top: 1px solid #e5eaf5;
}
.rate-market-title {
  margin-bottom: 14px;
  color: #111c3d;
  font-size: 14px;
  font-weight: 600;
}
.rate-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.rate-column {
  min-width: 0;
  padding-right: 30px;
}
.rate-column-divider {
  padding-right: 0;
  padding-left: 30px;
  border-left: 1px solid #dfe7f3;
}
.rate-column-title {
  margin-bottom: 12px;
  color: #111c3d;
  font-weight: 500;
}
.required-mark {
  color: #f56c6c;
}
.rate-field-label {
  margin-bottom: 7px;
  color: #5f6b85;
  font-size: 13px;
}
.rate-input {
  width: 100%;
  max-width: 260px;
  margin-bottom: 12px;
}
.specific-rate-input {
  margin-bottom: 0;
}
.rate-explanation {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #b9d5ff;
  border-radius: 4px;
  background: #f4f8ff;
  color: #3f5f8f;
  font-size: 13px;
  line-height: 20px;
}
.rate-explanation-icon {
  margin-top: 3px;
  color: #409eff;
  font-size: 15px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.history-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
